/**
 * Share basket modal state and submit handler.
 * Same API calls and payloads as previous inline logic.
 */
import * as React from 'react';

import { CART_CREATE_SHARE_TOKEN, EMAIL_SHARE_BASKET } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import { SHARE_ERROR_MESSAGES } from '../../shared-basket/constants/messages';
import { CartView } from '../types/cartTypes';

interface ShareRecipient {
  firstName: string;
  email: string;
}

interface ShareBasketData {
  recipient: ShareRecipient;
  message?: string;
}

export function useCartPageShare({ cart, showError }: { cart: CartView | null, showError: (msg: string) => void }) {
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);

  const openShareModal = React.useCallback(() => setIsShareModalOpen(true), []);
  const closeShareModal = React.useCallback(() => setIsShareModalOpen(false), []);

  const handleShareSubmit = React.useCallback(
    async (shareData: ShareBasketData) => {
      try {
        if (!cart || !cart.id) {
          throw new Error('No active cart to share');
        }

        let senderName = 'Someone';
        let senderEmail = 'shared-basket@informa.com';
        const pingUserStr = sessionStorage.getItem('ping_user_info') || localStorage.getItem('ping_user_info');
        if (pingUserStr) {
          try {
            const userInfo = JSON.parse(pingUserStr);
            senderName =
              `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() ||
              userInfo.name ||
              'Someone';
            senderEmail = userInfo.email || senderEmail;
          } catch (e) {
            console.warn('[useCartPageShare] Failed to parse ping_user_info JSON:', e);
            // Ignore error \u2014 sender info will use defaults
          }
        }

        const tokenResponse = await api.post(CART_CREATE_SHARE_TOKEN, {
          cartId: cart.id,
          senderEmail,
          senderName,
        });

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          throw new Error(errorData.error || 'Failed to create share link');
        }

        const { shareUrl } = await tokenResponse.json();
        const recipientFirstName = shareData.recipient.firstName;

        const emailResponse = await api.post(EMAIL_SHARE_BASKET, {
          senderName,
          recipientEmail: shareData.recipient.email,
          recipientName: recipientFirstName,
          message: shareData.message || '',
          shareUrl,
        });

        if (!emailResponse.ok) {
          const errorData = await emailResponse.json();
          throw new Error(errorData.error || 'Failed to send email');
        }

        setIsShareModalOpen(false);
      } catch (error) {
        if ((error as Error).message.includes('Failed to create share link')) {
          showError(SHARE_ERROR_MESSAGES.CREATE_LINK_FAILED);
        } else if ((error as Error).message.includes('Failed to send email')) {
          showError(SHARE_ERROR_MESSAGES.SEND_EMAIL_FAILED);
        } else if ((error as Error).message.includes('No active cart')) {
          showError(SHARE_ERROR_MESSAGES.NO_CART);
        } else {
          showError(SHARE_ERROR_MESSAGES.GENERIC_ERROR);
        }
        throw error;
      }
    },
    [cart, showError]
  );

  const handleShareError = React.useCallback((_error: Error | unknown) => {
  }, []);

  return {
    isShareModalOpen,
    openShareModal,
    closeShareModal,
    handleShareSubmit,
    handleShareError,
  };
}
